import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, utm } from "../../lib/api";
import { useSite } from "../../lib/site";
import { Field, inputCls } from "../common";

export default function LeadForm({ defaultProgram = "", form = "Contact", compact = false, title }) {
  const { programs } = useSite();
  const [d, setD] = useState({ first_name: "", last_name: "", email: "", phone: "", program_id: defaultProgram, format: "", start_date: "", funding: "", mycaa: false, message: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setD({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/leads", { ...d, form, ...utm() });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return (
    <div className="card p-8 text-center" data-testid="lead-form-success">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
      <h3 className="mt-3 font-heading text-xl font-bold">Thank you — we've received your request.</h3>
      <p className="mt-2 text-sm text-slate-600">An admissions team member will contact you soon. A confirmation has been sent to your email.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="card space-y-4 p-6 sm:p-8" data-testid="lead-form">
      {title && <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" required><input required className={inputCls} value={d.first_name} onChange={set("first_name")} data-testid="lead-first-name" autoComplete="given-name" /></Field>
        <Field label="Last name" required><input required className={inputCls} value={d.last_name} onChange={set("last_name")} data-testid="lead-last-name" autoComplete="family-name" /></Field>
        <Field label="Email" required><input required type="email" className={inputCls} value={d.email} onChange={set("email")} data-testid="lead-email" autoComplete="email" /></Field>
        <Field label="Phone" required><input required type="tel" className={inputCls} value={d.phone} onChange={set("phone")} data-testid="lead-phone" autoComplete="tel" /></Field>
      </div>
      <Field label="Program interested in">
        <select className={inputCls} value={d.program_id} onChange={set("program_id")} data-testid="lead-program">
          <option value="">Not sure yet</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      {!compact && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Learning format"><select className={inputCls} value={d.format} onChange={set("format")} data-testid="lead-format"><option value="">No preference</option><option>In-Person</option><option>Hybrid</option></select></Field>
          <Field label="Preferred start"><select className={inputCls} value={d.start_date} onChange={set("start_date")} data-testid="lead-start"><option value="">Flexible</option><option>As soon as possible</option><option>Within 1–3 months</option><option>3+ months</option></select></Field>
          <Field label="Funding"><select className={inputCls} value={d.funding} onChange={set("funding")} data-testid="lead-funding"><option value="">Not sure</option><option>Self Pay</option><option>SpouseWorks</option></select></Field>
        </div>
      )}
      {!compact && (
        <div className="flex flex-wrap gap-5 text-sm text-slate-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={d.mycaa} onChange={set("mycaa")} data-testid="lead-mycaa" className="h-4 w-4 accent-red-600" />I'm a military spouse (SpouseWorks)</label>
        </div>
      )}
      <Field label="Message"><textarea rows={compact ? 2 : 3} className={inputCls} value={d.message} onChange={set("message")} data-testid="lead-message" /></Field>
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input type="checkbox" required checked={d.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-red-600" data-testid="lead-consent" />
        I agree to be contacted by Healthcare Resources by phone, email or text about programs and enrollment. See our privacy policy.
      </label>
      <button disabled={busy} className="btn-primary w-full" data-testid="lead-submit">{busy ? "Sending…" : "Request Information"}</button>
    </form>
  );
}
